import { api } from '../../api'
import type {
  BulkAddSellersToWhitelistPayload,
  CreateSellerCommentPayload,
  CreateSellerPayload,
  GetSellerByIdParams,
  GetSellerItemsDirectParams,
  GetSellerItemsParams,
  ItemLite,
  ItemsPage,
  PaginatedResponse,
  Seller,
  SellerComment,
  SellerFilters,
  SellerItemExportPayload,
  SellerItemExportStatus,
  SellerWithExpand,
  SetUnderInvestigationPayload,
  SetUnderInvestigationResponse,
  UpdateSellerPayload,
} from '../../../types/seller.types'

// ─── Seller CRUD ──────────────────────────────────────────────────────────────

export const getSellers = async (params: SellerFilters): Promise<PaginatedResponse<Seller>> => {
  const res = await api.get<PaginatedResponse<Seller>>('sellers/', { params })
  return res.data
}

export const getSellersCount = async (params: SellerFilters): Promise<{ count: number }> => {
  const res = await api.get<{ count: number }>('sellers/count/', { params })
  return res.data
}

export const createSeller = async (data: CreateSellerPayload): Promise<Seller> => {
  const res = await api.post<Seller>('sellers/', data)
  return res.data
}

export const editSeller = async ({ id, ...partial }: UpdateSellerPayload): Promise<Seller> => {
  const res = await api.patch<Seller>(`sellers/${id}/`, partial)
  return res.data
}

export const deleteSeller = async (sellerId: string): Promise<unknown> => {
  const res = await api.delete(`sellers/${sellerId}/`)
  return res.data
}

export const mergeSellers = async (sellerIds: string[]): Promise<unknown> => {
  const res = await api.patch('sellers/merge/', sellerIds)
  return res.data
}

// ─── Seller Detail / Expand ───────────────────────────────────────────────────

export const getSellerByIdExpanded = async ({
  seller_id,
  expand,
  fields,
}: GetSellerByIdParams): Promise<SellerWithExpand | null> => {
  const res = await api.get<SellerWithExpand>(`sellers/${seller_id}/`, {
    params: { expand, fields },
  })
  return res.data ?? null
}

export const getSellerItemsViaExpand = async ({
  seller_id,
  data,
}: GetSellerItemsParams): Promise<ItemsPage> => {
  const params = { ...data, expand: 'items,whitelisted_by_brand,whitelisted_by_member' }
  const res = await api.get<PaginatedResponse<SellerWithExpand>>(`sellers/${seller_id}/`, {
    params: {
      page_size: 1,
      items_page_size: (params as any)?.limit ?? 100,
      items_cursor: (params as any)?.offset ?? null,
      ...params,
    },
  })

  const seller = res.data.data?.[0] as SellerWithExpand | undefined
  const itemsBlock = (seller as any)?.items ?? {}
  const items = (itemsBlock?.data as ItemLite[]) ?? []
  const hasNext = Boolean(itemsBlock?.hasNext)
  const nextCursor = (itemsBlock?.nextCursor as number | null) ?? null
  const totalAmount = (itemsBlock?.total_amount as number) ?? 0

  return {
    data: items,
    hasNext,
    nextPage: nextCursor,
    total_amount: totalAmount,
    offset: nextCursor,
    lastPage: !hasNext,
  }
}

export const getSellerItems = async ({
  id,
  data,
}: GetSellerItemsDirectParams): Promise<ItemsPage> => {
  const res = await api.get<PaginatedResponse<ItemLite>>(`items/`, {
    params: { ...data, seller_id: id },
  })
  return res.data
}

// ─── Investigation ────────────────────────────────────────────────────────────

export const setSellerUnderInvestigation = async (
  data: SetUnderInvestigationPayload
): Promise<SetUnderInvestigationResponse> => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const payload: any = {
    referenced_seller: data.seller_id,
    label: data.label,
    detail: data.detail,
  }
  if (data.case_number && data.case_number.trim() !== '') payload.case_number = data.case_number
  if (data.test_purchase !== undefined) payload.test_purchase = data.test_purchase
  if (data.brand_id !== undefined) payload.brand = data.brand_id

  const res = await api.post<SetUnderInvestigationResponse>('cases/', payload)
  return res.data
}

// ─── Whitelist ────────────────────────────────────────────────────────────────

export const addWhitelistedSellerGlobal = async (sellerId: string): Promise<unknown> => {
  const res = await api.post('sellers/whitelisted/', { id: sellerId })
  return res.data
}

export const addWhitelistedSellerForMember = async (memberId: string, sellerId: string) => {
  const res = await api.post(`members/${memberId}/whitelisted_sellers/`, { id: sellerId })
  return res.data
}

export const removeWhitelistedSellerGlobal = async (sellerId: string): Promise<unknown> => {
  const res = await api.delete(`sellers/whitelisted/${sellerId}/`)
  return res.data
}

export const removeWhitelistedSellerForMember = async (memberId: string, sellerId: string) => {
  const res = await api.delete(`members/${memberId}/whitelisted_sellers/${sellerId}/`)
  return res.data
}

export const removeWhitelistedSellerForBrand = async (brandId: string, sellerId: string) => {
  const res = await api.delete(`members/brands/${brandId}/whitelisted_sellers/${sellerId}/`)
  return res.data
}

export const bulkAddSellersToWhitelist = async (data: BulkAddSellersToWhitelistPayload) => {
  const createRes = await api.post(
    'sellers/whitelisted/bulk_create/',
    data.sellers.map((url) => {
      const trimmed = url.trim()
      return { url: trimmed && !/^https?:\/\//i.test(trimmed) ? `https://${trimmed}` : trimmed }
    })
  )

  const createdSellers = createRes.data

  if (data.scope === 'global') {
    return { data: createdSellers }
  } else if (data.scope === 'member' && data.memberId) {
    const results = []
    for (const seller of createdSellers) {
      const res = await api.post(`members/${data.memberId}/whitelisted_sellers/`, { id: seller.id })
      results.push(res.data)
    }
    return { data: results }
  } else if (data.scope === 'brand' && data.brandIds && data.brandIds.length > 0) {
    const results = []
    for (const brandId of data.brandIds) {
      for (const seller of createdSellers) {
        const res = await api.post(`members/brands/${brandId}/whitelisted_sellers/`, { id: seller.id })
        results.push(res.data)
      }
    }
    return { data: results }
  } else {
    return { error: 'Invalid scope or missing IDs for member/brand whitelisting.' }
  }
}

// ─── Seller Item Export ───────────────────────────────────────────────────────

export const createSellerItemExport = async (data: SellerItemExportPayload): Promise<SellerItemExportStatus> => {
  const res = await api.post<SellerItemExportStatus>('exports/seller_items/', data)
  return res.data
}

export const getSellerItemExportStatus = async (id: string): Promise<SellerItemExportStatus> => {
  const res = await api.get<SellerItemExportStatus>(`exports/seller_items/${id}/`)
  return res.data
}

export const abortSellerItemExport = async (id: string): Promise<void> => {
  await api.patch(`exports/seller_items/${id}/abort/`)
}

// ─── Comments ─────────────────────────────────────────────────────────────────

export const getSellerComments = async (sellerId: string): Promise<SellerComment[]> => {
  const res = await api.get<PaginatedResponse<SellerComment>>(`sellers/${sellerId}/comments/`, {
    params: { expand: 'author' },
  })
  return res.data.data
}

export const createSellerComment = async (
  sellerId: string,
  data: CreateSellerCommentPayload
): Promise<SellerComment> => {
  const res = await api.post<SellerComment>(`sellers/${sellerId}/comments/`, data)
  return res.data
}

export const updateSellerComment = async (
  sellerId: string,
  commentId: string,
  data: CreateSellerCommentPayload
): Promise<SellerComment> => {
  const res = await api.patch<SellerComment>(`sellers/${sellerId}/comments/${commentId}/`, data)
  return res.data
}

export const deleteSellerComment = async (sellerId: string, commentId: string): Promise<void> => {
  await api.delete(`sellers/${sellerId}/comments/${commentId}/`)
}

export const bulkCreateSellers = async (data: CreateSellerPayload[]): Promise<Seller[]> => {
  const res = await api.post<Seller[]>('sellers/bulk_create/', data)
  return res.data
}
